import React from 'react';
import { useNode } from '@craftjs/core';

interface MapEmbedProps {
  address?: string;
  zoom?: number;
  height?: number;
  maxWidth?: number;
  borderRadius?: number;
  margin?: number;
}

/**
 * URL del mapa embebido de Google.
 *
 * Usa `output=embed`, que NO necesita API key ni facturación — la Maps Embed API
 * con key es otra cosa y no hace falta para mostrar una dirección.
 *
 * MANTENER EN SYNC con goautos-admin.
 */
export const urlDelMapa = (direccion: string, zoom: number) => {
  const texto = (direccion || '').trim();
  if (!texto) return null;
  const z = Math.max(1, Math.min(20, Number(zoom) || 15));
  return `https://www.google.com/maps?q=${encodeURIComponent(texto)}&z=${z}&output=embed`;
};

/**
 * Mapa — render del sitio público.
 *
 * Solo el mapa, para soltarlo donde haga falta. `HowToArrive` sigue siendo la
 * sección completa con horarios y datos de contacto; esto es el bloque suelto.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/MapEmbed.tsx.
 */
export const MapEmbed = ({
  address = '',
  zoom = 15,
  height = 320,
  maxWidth = 0,
  borderRadius = 12,
  margin = 0,
}: MapEmbedProps) => {
  const { connectors } = useNode();

  const url = urlDelMapa(address, zoom);
  const alto = Math.max(120, Number(height) || 320);
  const ancho = Math.max(0, Number(maxWidth) || 0);

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      style={{
        width: '100%',
        margin: `${margin}px auto`,
        ...(ancho > 0 ? { maxWidth: `${ancho}px` } : {}),
        // Igual que en `Section`: el editor lo necesita para anclar sus controles
        // y acá no cambia nada, pero mantiene idéntico el estilo computado.
        position: 'relative',
      }}
    >
      <div
        style={{
          width: '100%',
          height: `${alto}px`,
          borderRadius: `${borderRadius}px`,
          overflow: 'hidden',
          background: '#e5e7eb',
        }}
      >
        {url && (
          <iframe
            src={url}
            title={address}
            style={{ width: '100%', height: '100%', border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        )}
      </div>
    </div>
  );
};

MapEmbed.craft = {
  displayName: 'MapEmbed',
  props: {
    address: '',
    zoom: 15,
    height: 320,
    maxWidth: 0,
    borderRadius: 12,
    margin: 0,
  },
};
